/**
 * Resolve root-relative public asset paths for src, href, and CSS url().
 *
 * Production builds set homepage to "./", so many call sites used
 * `url(${process.env.PUBLIC_URL}/images/foo.png)` → `url(./images/foo.png)`.
 * When that value is stored in a CSS custom property and consumed from a bundled
 * stylesheet (e.g. Welcome.css, MenuPaintedButton.css), browsers resolve the
 * relative URL against static/css/ — not index.html — so painted panels/buttons
 * 404 in web and Electron builds even though the files exist in build/images/.
 */

/**
 * @param {string} assetPath - Root-relative path, e.g. /images/red_button4.png
 * @returns {string}
 */
export function resolvePublicAssetUrl(assetPath) {
  if (!assetPath || typeof assetPath !== 'string') {
    throw new Error('resolvePublicAssetUrl: assetPath must be a non-empty string');
  }
  if (!assetPath.startsWith('/')) {
    throw new Error(
      `resolvePublicAssetUrl: path must be root-relative (start with /): ${assetPath}`
    );
  }

  if (typeof window !== 'undefined' && window.location && window.location.href) {
    if (window.location.protocol === 'file:') {
      // `/images/…` joined to a file:// document must stay under app.asar/index.html,
      // not resolve to file:///D:/images/… (drive root), which the protocol handler
      // may not rewrite for <img src> requests.
      return new URL(`.${assetPath}`, window.location.href).href;
    }
    return new URL(assetPath, window.location.href).href;
  }

  return assetPath;
}

/**
 * @param {string} assetPath - Root-relative path, e.g. /images/red_button4.png
 * @returns {string} CSS background-image value, e.g. url("http://…/images/foo.png")
 */
export function resolvePublicAssetBackgroundUrl(assetPath) {
  return `url("${resolvePublicAssetUrl(assetPath)}")`;
}
